/* Movatic WMS — Coletor | TaskOfferPopup (pop-up de interrupcao) | CesarFilho 932048016
   Arquivo: src/mobile/components/TaskOfferPopup.tsx
   ----------------------------------------------------------------------------
   ADR-0034 F4a (= ADR-0038 F7). A tarefa e' interrupcao de prioridade maxima: pop-up
   AMARELO full-screen (o amarelo E' o sinal de convocacao), bloqueia tudo, aceitar/
   rejeitar obrigatorio. O operador ACEITA A'S CEGAS (ADR-0038/0040): NAO ve o conteudo
   da tarefa antes de decidir -> mostra so' o id neutro (Tarefa #NNN) + cronometro, NUNCA
   tipo/linhas/produto/destino (senao escolheria as faceis, rejeitando as dificeis). Ao
   APARECER, alerta som+vibracao PERSISTENTE ~15s (ou ate' decidir). Cores HARDCODED: o
   popup monta FORA do .coletor-root (irmao do MobileShell) -> os tokens var(--*) desse
   scope nao resolvem aqui. Apresentacional: delega via onAccept/onReject (o provider faz
   o wiring); Rejeitar -> bottom-sheet de motivos (catalogo RejectionReason). */
import { useEffect, useState } from 'react'
import { ClipboardList } from 'lucide-react'
import {
  getRejectionReasons,
  type RejectionReasonDTO,
  type TaskDTO,
} from '@/api/workforce'
import { useFeedback } from '../hooks/useFeedback'

type Props = {
  task: TaskDTO
  onAccept: () => void
  onReject: (reasonId: number) => void
  busy?: boolean
  /** ADR-0058: motivo do backend (ex.: reject de uma task já concluída). Mostrado no popup. */
  error?: string | null
}

function mmss(s: number) {
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`
}

export default function TaskOfferPopup({
  task,
  onAccept,
  onReject,
  busy,
  error,
}: Props) {
  const [elapsed, setElapsed] = useState(0) // cronometro conta para CIMA (KPI tempo de resposta)
  const [sheet, setSheet] = useState(false) // bottom-sheet de motivos
  const [reasons, setReasons] = useState<RejectionReasonDTO[]>([])
  const [sel, setSel] = useState<number | null>(null)
  const fb = useFeedback()

  // Cronometro (KPI tempo de resposta, so' DISPLAY -- nao ha' auto-rejeicao). PAUSA quando a sheet
  // de rejeicao abre: o operador ja' respondeu (escolhe o motivo) e a sheet cobre o mostrador ->
  // o setElapsed deixa de re-renderizar os botoes de motivo por baixo do dedo (o tap passa a
  // registar de forma fiavel). Retoma ao fechar a sheet (Voltar).
  useEffect(() => {
    if (sheet) return
    const t = setInterval(() => setElapsed((e) => e + 1), 1000)
    return () => clearInterval(t)
  }, [sheet])

  // Alerta PERSISTENTE ao APARECER a oferta: som (3 beeps) + vibracao, repetido a cada 2s durante
  // ~15s OU ate' o operador Aceitar/Rejeitar (o popup desmonta -> o cleanup para tudo). PARA
  // tambem quando a sheet de rejeicao ABRE: o operador ja' respondeu (carregou Rejeitar) -> nao faz
  // sentido continuar a alarmar/vibrar enquanto escolhe o motivo, e a vibracao a cada 2s comia o
  // tap nos botoes. Gate por `sheet`: ao abrir, o cleanup limpa o interval (a vibracao em curso e'
  // cancelada no handler do Rejeitar via fb.haptic(0)). Retoma se a sheet fechar (Voltar).
  useEffect(() => {
    if (sheet) return
    fb.alarm() // imediato
    const iv = setInterval(() => fb.alarm(), 2000)
    const stop = setTimeout(() => clearInterval(iv), 15000)
    return () => {
      clearInterval(iv)
      clearTimeout(stop)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ao montar + parar quando a sheet abre
  }, [sheet])

  useEffect(() => {
    if (sheet && reasons.length === 0) {
      getRejectionReasons()
        .then(setReasons)
        .catch(() => {})
    }
  }, [sheet, reasons.length])

  // ACEITA A'S CEGAS: so' o id neutro (nao revela tipo/linhas/produto/destino -> senao o
  // operador escolheria as tarefas, o que e' proibido: ele aceita/rejeita, nao seleciona).
  const titulo = String(
    (task as any).title ?? (task as any).reference ?? `Tarefa #${task.id}`,
  )

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-between p-6 select-none"
      style={{ background: 'linear-gradient(165deg, #facc15, #eab308)' }}
    >
      {/* ADR-0058: motivo do backend, visível ACIMA da bottom-sheet (z-[60]) -> nunca engolido. */}
      {error && (
        <div
          className="fixed top-0 inset-x-0 z-[70] px-4 py-3 text-center text-sm font-medium text-white"
          style={{ background: '#7a1f1f' }}
        >
          {error}
        </div>
      )}

      <div
        className="mt-4 px-3 py-1 rounded-full text-xs font-medium"
        style={{ background: 'rgba(0,0,0,.18)', color: '#3f2d00' }}
      >
        NOVA TAREFA
      </div>

      <div className="flex flex-col items-center gap-3 text-center">
        <div
          className="grid place-items-center rounded-2xl"
          style={{ width: 74, height: 74, background: 'rgba(0,0,0,.18)' }}
        >
          <ClipboardList size={40} color="#fff" />
        </div>
        <div className="text-[21px] font-medium" style={{ color: '#3f2d00' }}>
          {titulo}
        </div>
        <div
          className="mt-2 px-4 py-2 rounded-xl tabular-nums text-white"
          style={{ fontSize: 34, background: 'rgba(0,0,0,.2)' }}
        >
          {mmss(elapsed)}
        </div>
      </div>

      <div className="w-full max-w-sm grid gap-3 mb-2">
        <button
          disabled={busy}
          onClick={() => {
            fb.click()
            onAccept()
          }}
          className="w-full py-3 rounded-xl font-semibold"
          style={{ background: '#fff', color: '#7a5a00' }}
        >
          Aceitar
        </button>
        <button
          disabled={busy}
          onClick={() => {
            fb.haptic(0) // cancela a vibracao em curso do alarme (navigator.vibrate(0))
            setSheet(true)
          }}
          className="w-full py-3 rounded-xl font-medium text-white"
          style={{ background: 'rgba(0,0,0,.16)' }}
        >
          Rejeitar
        </button>
      </div>

      {sheet && (
        <div
          className="fixed inset-0 z-[60] flex flex-col justify-end"
          style={{ background: 'rgba(0,0,0,.5)' }}
        >
          <div
            className="rounded-t-2xl p-4 grid gap-2"
            style={{ background: '#101018' }}
          >
            <div
              className="text-sm font-medium text-center mb-1"
              style={{ color: '#eaf1fa' }}
            >
              Motivo da rejeição
            </div>
            {reasons.map((r) => (
              <button
                key={r.id}
                onClick={() => setSel(r.id)}
                className="w-full py-2 rounded-lg text-left px-3"
                style={{
                  background: sel === r.id ? '#16213b' : '#13192a',
                  color: '#eaf1fa',
                }}
              >
                {r.name}
              </button>
            ))}
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button
                onClick={() => {
                  setSheet(false)
                  setSel(null)
                }}
                className="py-2 rounded-lg text-white"
                style={{ background: 'rgba(255,255,255,.12)' }}
              >
                Voltar
              </button>
              <button
                disabled={sel === null || busy}
                onClick={() => sel != null && onReject(sel)}
                className="py-2 rounded-lg font-semibold"
                style={{
                  background: '#7a1f1f',
                  color: '#f5b5b5',
                  opacity: sel === null ? 0.5 : 1,
                }}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
